/**
 * use-supabase-core.ts — Generic query/mutation infrastructure
 * Split from use-supabase.ts per #566.
 * #710: All queries await auth-ready gate before executing.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { createLogger } from '@/lib/logger';
import { cachedFetch, readCache, invalidateCache } from '@/lib/query-cache';
import { waitForAuth } from '@/lib/supabase';

const log = createLogger('UseSupabase');

// ── Generic hook result ──────────────────────────────

export interface UseQueryResult<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => void;
}

export function useSupabaseQuery<T>(
  queryFn: (() => Promise<T>) | null,
  deps: (string | number | boolean | null | undefined)[] = [],
  /** Optional cache key. If provided, enables SWR caching. */
  cacheKey?: string,
): UseQueryResult<T> {
  // Try synchronous cache read for instant render (stale-while-revalidate)
  const initialData = cacheKey ? readCache<T>(cacheKey) : undefined;
  const [data, setData] = useState<T | undefined>(initialData);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(initialData ? false : !!queryFn);
  const mountedRef = useRef(true);
  const fetchIdRef = useRef(0);
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;
  const dataRef = useRef(data);
  dataRef.current = data;
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;

  const execute = useCallback(async () => {
    const fn = queryFnRef.current;
    if (!fn) {
      setData(undefined);
      setLoading(false);
      return;
    }
    // #710: Wait for auth session recovery before any Supabase query
    await waitForAuth();
    const id = ++fetchIdRef.current;
    // #700: Use ref to check current data value, not stale closure capture
    if (!dataRef.current) setLoading(true);
    setError(undefined);

    const MAX_RETRIES = 2;
    let lastErr: Error | undefined;

    const doFetch = async (): Promise<T> => {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (!mountedRef.current || id !== fetchIdRef.current) throw new Error('aborted');
        try {
          return await fn();
        } catch (err) {
          lastErr = err instanceof Error ? err : new Error(String(err));
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
            if (!mountedRef.current || id !== fetchIdRef.current) throw new Error('aborted');
          }
        }
      }
      throw lastErr ?? new Error('fetch failed');
    };

    try {
      const result = cacheKey
        ? await cachedFetch(cacheKey, doFetch)
        : await doFetch();
      if (mountedRef.current && id === fetchIdRef.current) {
        setData(result);
        setLoading(false);
      }
    } catch (err) {
      if (mountedRef.current && id === fetchIdRef.current) {
        if (err instanceof Error && err.message === 'aborted') return;
        log.error('Query failed', { err: (err as Error).message });
        setError(err as Error);
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Invalidate cache on re-mount so route switches always get fresh data
  useEffect(() => {
    mountedRef.current = true;
    if (cacheKeyRef.current) {
      invalidateCache(cacheKeyRef.current);
    }
    execute();
    return () => { mountedRef.current = false; };
  }, [execute]);

  // Force refresh: invalidate cache first so cachedFetch does a real fetch
  const forceRefresh = useCallback(() => {
    if (cacheKeyRef.current) invalidateCache(cacheKeyRef.current);
    execute();
  }, [execute]);

  return { data, error, loading, refetch: forceRefresh };
}

// ── Mutations ────────────────────────────────────────

export interface UseMutationResult<TInput, TOutput> {
  mutate: (input: TInput) => Promise<TOutput>;
  loading: boolean;
  error: Error | undefined;
  data: TOutput | undefined;
}

export function useMutation<TInput, TOutput>(
  mutationFn: (input: TInput) => Promise<TOutput>,
): UseMutationResult<TInput, TOutput> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<TOutput | undefined>(undefined);
  // #287: Use ref to avoid stale closure capturing old mutationFn
  const fnRef = useRef(mutationFn);
  fnRef.current = mutationFn;

  const mutate = useCallback(
    async (input: TInput) => {
      setLoading(true);
      setError(undefined);
      try {
        const result = await fnRef.current(input);
        setData(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        log.error('Mutation failed', { err: e.message });
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { mutate, loading, error, data };
}
